import React, { useEffect, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { API_ENDPOINTS, buildApiUrl } from '../config/apiConfig';

const SOURCE_KEY = {
  Direct: 'adminAnalytics.direct',
  Google: 'adminAnalytics.google',
  LinkedIn: 'adminAnalytics.linkedin',
  X: 'adminAnalytics.x',
  External: 'adminAnalytics.external',
};

const DEVICE_KEY = {
  Mobile: 'adminAnalytics.mobile',
  Desktop: 'adminAnalytics.desktop',
  Tablet: 'adminAnalytics.tablet',
  Unknown: 'adminAnalytics.unknown',
};

const AdminAnalyticsSection = () => {
  const { t, isRTL, language } = useLanguage();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');

  const formatTime = (iso) => {
    if (!iso) return t('adminAnalytics.dash');
    try {
      return new Date(iso).toLocaleString(language === 'ar' ? 'ar-SA' : 'en-US', {
        dateStyle: 'short',
        timeStyle: 'medium',
      });
    } catch {
      return t('adminAnalytics.dash');
    }
  };

  const translateSource = (source) => {
    if (source == null || source === '') return t('adminAnalytics.dash');
    const k = SOURCE_KEY[source];
    return k ? t(k) : source;
  };

  const translateDevice = (device) => {
    if (device == null || device === '') return t('adminAnalytics.dash');
    const k = DEVICE_KEY[device];
    return k ? t(k) : device;
  };

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await fetch(buildApiUrl(API_ENDPOINTS.ADMIN_ANALYTICS), {
          credentials: 'include',
        });
        if (!res.ok) {
          if (!cancel) setErr('failed');
          return;
        }
        const json = await res.json();
        if (!cancel) {
          setData(json);
          setErr('');
        }
      } catch {
        if (!cancel) setErr('failed');
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);

  if (err) {
    return (
      <div
        className="admin-analytics-block admin-analytics-card-skin admin-analytics--ydim"
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <div className="admin-state admin-state--error" role="status">
          <p className="admin-state__message">{t('adminAnalytics.failed')}</p>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div
        className="admin-analytics-block admin-analytics-card-skin admin-analytics--ydim"
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <div className="admin-state admin-state--loading" aria-live="polite">
          <div className="admin-loading-dots" aria-hidden>
            <span />
            <span />
            <span />
          </div>
          <p className="admin-state__message">{t('adminAnalytics.loading')}</p>
        </div>
      </div>
    );
  }

  const maxDaily = Math.max(1, ...(data.dailyVisits || []).map((d) => d.visitors || 0));
  const conv = data.conversions || {};
  const dash = t('adminAnalytics.dash');

  const recent = data.recentEvents || [];
  const recentTop = recent.slice(0, 5);
  const recentRest = recent.slice(5);

  return (
    <div className="admin-analytics admin-analytics-block admin-analytics--ydim" dir={isRTL ? 'rtl' : 'ltr'}>
      <header className="admin-analytics-hero">
        <h2 className="admin-analytics-title" id="admin-ydim-analytics-title">
          {t('adminAnalytics.title')}
        </h2>
        <p className="admin-analytics-subtitle">{t('adminAnalytics.ydimBlurb')}</p>
      </header>

      <div className="admin-summary admin-summary--metrics">
        <div className="summary-card">
          <div className="summary-label">{t('adminAnalytics.totalPageViews')}</div>
          <div className="summary-value">{data.totalPageViews ?? 0}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">{t('adminAnalytics.pageRefreshes')}</div>
          <div className="summary-value">{data.totalRefreshes ?? 0}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">{t('adminAnalytics.adminVisits')}</div>
          <div className="summary-value">{data.adminVisits ?? 0}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">{t('adminAnalytics.adminRefreshes')}</div>
          <div className="summary-value">{data.adminRefreshes ?? 0}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">{t('adminAnalytics.totalVisitors')}</div>
          <div className="summary-value">{data.totalVisitors ?? 0}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">{t('adminAnalytics.registeredSessions')}</div>
          <div className="summary-value">{data.registeredVisitorSessions ?? 0}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">{t('adminAnalytics.guestVisitors')}</div>
          <div className="summary-value">{data.guestVisitors ?? 0}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">{t('adminAnalytics.registeredUsers')}</div>
          <div className="summary-value">{data.registeredUsers ?? 0}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">{t('adminAnalytics.todayVisitors')}</div>
          <div className="summary-value">{data.todayVisitors ?? 0}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">{t('adminAnalytics.newRegistrations30d')}</div>
          <div className="summary-value">{data.signups?.newRegistrations30d ?? 0}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">{t('adminAnalytics.conversionRate')}</div>
          <div className="summary-value">
            {conv.conversionRatePercent != null ? `${conv.conversionRatePercent}%` : dash}
          </div>
        </div>
      </div>

      <div className="admin-analytics-grid">
        <section className="admin-analytics-panel">
          <h3>{t('adminAnalytics.dailyVisitors30d')}</h3>
          <div className="admin-bar-chart">
            {(data.dailyVisits || []).length === 0 ? (
              <p className="admin-analytics-no-data">{t('adminAnalytics.noData')}</p>
            ) : (
              (data.dailyVisits || []).map((row) => (
                <div key={row.day} className="admin-bar-row">
                  <span className="admin-bar-label">{row.day}</span>
                  <div className="admin-bar-track">
                    <div
                      className="admin-bar-fill"
                      style={{ width: `${(row.visitors / maxDaily) * 100}%` }}
                    />
                  </div>
                  <span className="admin-bar-val">{row.visitors}</span>
                </div>
              ))
            )}
          </div>
        </section>

        <section className="admin-analytics-panel">
          <h3>{t('adminAnalytics.monthlyVisitors')}</h3>
          {(data.monthlyVisits || []).length === 0 ? (
            <p className="admin-analytics-no-data">{t('adminAnalytics.noData')}</p>
          ) : (
            <ul className="admin-analytics-list">
              {(data.monthlyVisits || []).map((row) => (
                <li key={row.month}>
                  <span>{row.month}</span> <strong>{row.visitors}</strong>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <div className="admin-analytics-tables">
        <section className="admin-analytics-panel admin-table-card">
          <h3>{t('adminAnalytics.topPages')}</h3>
          <div className="interactions-table-container">
            <table className="interactions-table">
              <thead>
                <tr>
                  <th>{t('adminAnalytics.page')}</th>
                  <th>{t('adminAnalytics.views')}</th>
                </tr>
              </thead>
              <tbody>
                {(data.topPages || []).length === 0 ? (
                  <tr>
                    <td colSpan="2" className="no-data">
                      {t('adminAnalytics.noData')}
                    </td>
                  </tr>
                ) : (
                  (data.topPages || []).map((p) => (
                    <tr key={p.page}>
                      <td>{p.page}</td>
                      <td>{p.views}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section className="admin-analytics-panel admin-table-card">
          <h3>{t('adminAnalytics.sources')}</h3>
          <div className="interactions-table-container">
            <table className="interactions-table">
              <thead>
                <tr>
                  <th>{t('adminAnalytics.source')}</th>
                  <th>{t('adminAnalytics.count')}</th>
                </tr>
              </thead>
              <tbody>
                {(data.sources || []).length === 0 ? (
                  <tr>
                    <td colSpan="2" className="no-data">
                      {t('adminAnalytics.noData')}
                    </td>
                  </tr>
                ) : (
                  (data.sources || []).map((s) => (
                    <tr key={s.source}>
                      <td>{translateSource(s.source)}</td>
                      <td>{s.c}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section className="admin-analytics-panel admin-table-card">
          <h3>{t('adminAnalytics.devices')}</h3>
          <div className="interactions-table-container">
            <table className="interactions-table">
              <thead>
                <tr>
                  <th>{t('adminAnalytics.device')}</th>
                  <th>{t('adminAnalytics.count')}</th>
                </tr>
              </thead>
              <tbody>
                {(data.devices || []).length === 0 ? (
                  <tr>
                    <td colSpan="2" className="no-data">
                      {t('adminAnalytics.noData')}
                    </td>
                  </tr>
                ) : (
                  (data.devices || []).map((d) => (
                    <tr key={d.device}>
                      <td>{translateDevice(d.device)}</td>
                      <td>{d.c}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <section className="admin-analytics-panel admin-table-card">
        <h3>{t('adminAnalytics.importantClicks')}</h3>
        <div className="interactions-table-container">
          <table className="interactions-table">
            <thead>
              <tr>
                <th>{t('adminAnalytics.event')}</th>
                <th>{t('adminAnalytics.count')}</th>
              </tr>
            </thead>
            <tbody>
              {(data.importantClicks || []).length === 0 ? (
                <tr>
                  <td colSpan="2" className="no-data">
                    {t('adminAnalytics.noData')}
                  </td>
                </tr>
              ) : (
                (data.importantClicks || []).map((e) => (
                  <tr key={e.event_type}>
                    <td className="monospace">{e.event_type}</td>
                    <td>{e.c}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="admin-analytics-panel admin-table-card admin-recent-events-section">
        <h3 id="admin-recent-events-h">{t('adminAnalytics.recentEvents')}</h3>
        {recent.length === 0 ? (
          <p className="admin-empty-hint" role="status">
            {t('adminAnalytics.noData')}
          </p>
        ) : (
          <div className="admin-table-surface" aria-describedby="admin-recent-events-h">
            <div className="interactions-table-container">
              <table className="interactions-table" role="table">
                <colgroup>
                  <col className="analytics-recent-col analytics-recent-col--t" />
                  <col className="analytics-recent-col analytics-recent-col--e" />
                  <col className="analytics-recent-col analytics-recent-col--p" />
                  <col className="analytics-recent-col analytics-recent-col--g" />
                  <col className="analytics-recent-col analytics-recent-col--s" />
                </colgroup>
                <thead>
                  <tr>
                    <th>{t('adminAnalytics.time')}</th>
                    <th>{t('adminAnalytics.type')}</th>
                    <th>{t('adminAnalytics.page')}</th>
                    <th>{t('adminAnalytics.guest')}</th>
                    <th>{t('adminAnalytics.session')}</th>
                  </tr>
                </thead>
                <tbody>
                  {recentTop.map((ev) => (
                    <tr key={ev.id}>
                      <td>{formatTime(ev.createdAt)}</td>
                      <td className="monospace">{ev.eventType}</td>
                      <td>{ev.page || dash}</td>
                      <td>{ev.isGuest ? t('adminAnalytics.yes') : t('adminAnalytics.no')}</td>
                      <td className="monospace">{ev.sessionPreview || dash}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {recentRest.length > 0 && (
              <div
                className="recent-events-scroll"
                role="region"
                aria-label={t('adminPage.scrollRegion')}
                tabIndex={0}
              >
                <table className="interactions-table interactions-table--cont">
                  <colgroup>
                    <col className="analytics-recent-col analytics-recent-col--t" />
                    <col className="analytics-recent-col analytics-recent-col--e" />
                    <col className="analytics-recent-col analytics-recent-col--p" />
                    <col className="analytics-recent-col analytics-recent-col--g" />
                    <col className="analytics-recent-col analytics-recent-col--s" />
                  </colgroup>
                  <tbody>
                    {recentRest.map((ev) => (
                      <tr key={ev.id}>
                        <td>{formatTime(ev.createdAt)}</td>
                        <td className="monospace">{ev.eventType}</td>
                        <td>{ev.page || dash}</td>
                        <td>{ev.isGuest ? t('adminAnalytics.yes') : t('adminAnalytics.no')}</td>
                        <td className="monospace">{ev.sessionPreview || dash}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
};

export default AdminAnalyticsSection;
